import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect} from 'react';

// @ts-expect-error
import * as UserActions from 'actions/UserActions';
import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {JoinOrganizationListingOrganization} from 'parties/organizations/__generated/JoinOrganizationListingOrganization';
import {
  JoinOrganizationSearchQuery_currentUser_UserNode_organizations_OrganizationNode as JoinOrganizationSearchUserOrganizaton,
  JoinOrganizationSearchQuery,
} from 'parties/organizations/__generated/JoinOrganizationSearchQuery';
import JoinOrganizationListing from 'parties/organizations/JoinOrganizationListing';
import ClearSearchButton from 'search/ClearSearchButton';
import FiltersContainer from 'search/filters/FiltersContainer';
import * as FilterUtil from 'search/FilterUtil';
import {Filter, FilterKey, InstitutionFilter} from 'search/SearchConstants';
import EntityStore from 'stores/EntityStore';
import {useDebounce} from 'ui-library/hooks/useDebounce';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {Size} from 'ui-library/inputs/InputConstants';
import TextInput from 'ui-library/inputs/TextInput';
import {InputChangeEvent} from 'util/Types';

import JoinOrganizationRecommendationsListing from './JoinOrganizationRecommendationsListing';
import JoinOrganizationSearchListing from './JoinOrganizationSearchListing';

type JoinOrganizationSearchUser = JoinOrganizationSearchQuery['currentUser'];

const LISTING_DEBOUNCE_MS = 250;
const LISTING_HEADER_DEBOUNCE_MS = 500;
const LIMIT_OFFSET = {limit: 15, offset: 0};

export interface JoinOrganizationSearchBaseProps {
  query: string;
  onChangeQuery: (query: string) => void;
  filters: Array<Filter>;
  setFilters: (filters: Array<Filter>) => void;
  requestedJoinOrganizationIds: Array<string>;
  canNavigateToOrganizationPage?: boolean;
  placeholderBodyText?: string;
  listingHeight?: number;
  showInstitutionFilter?: boolean;
}

type JoinOrganizationSearchProps = JoinOrganizationSearchBaseProps & {
  onChangeRequestedJoinOrganizationIds: (organizationIds: Array<string>) => void;
  hasSearched: boolean;
  showRecommendations?: boolean;
};

export default function JoinOrganizationSearch(props: JoinOrganizationSearchProps): JSX.Element {
  const debouncedQuery = useDebounce(props.query, LISTING_DEBOUNCE_MS);
  useEffect(() => {
    if (debouncedQuery) {
      analytics.track('organizations', 'searched_for_organization', {
        search_query: debouncedQuery,
        user_id: EntityStore.currentUser()?.id,
      });
    }
  }, [debouncedQuery]);
  const institutionFilter = FilterUtil.getFilterByKey(
    props.filters,
    FilterKey.INSTITUTION
  ) as InstitutionFilter;
  const queryVariables = {
    searchInput: {
      query: debouncedQuery,
      institutionId: institutionFilter?.value?.id,
    },
    limitOffsetInput: LIMIT_OFFSET,
  };
  const handleClickRequestJoinOrganization = (organizationId: string): void => {
    props.onChangeRequestedJoinOrganizationIds([...props.requestedJoinOrganizationIds, organizationId]);
    try {
      UserActions.createInboundRequest(organizationId);
      // oxlint-disable-next-line no-useless-catch
    } catch (e) {
      throw e;
    }
  };

  return !props.hasSearched && props.showRecommendations ? (
    <JoinOrganizationRecommendationsListing
      {...props}
      onClickRequestJoinOrganization={handleClickRequestJoinOrganization}
      showInstitutionFilter={props.showInstitutionFilter}
      filters={props.filters}
      setFilters={props.setFilters}
    />
  ) : (
    <JoinOrganizationSearchListing
      {...props}
      queryVariables={queryVariables}
      onClickRequestJoinOrganization={handleClickRequestJoinOrganization}
      filters={props.filters}
      setFilters={props.setFilters}
    />
  );
}

interface OrganizationSearchInputProps {
  query: string;
  onChangeQuery: (query: string) => void;
  showInstitutionFilter: boolean;
}

function OrganizationSearchInput(props: OrganizationSearchInputProps): JSX.Element {
  const handleChangeQuery = (e: InputChangeEvent): void => {
    props.onChangeQuery(e.target.value);
  };
  return (
    <TextInput
      className={classNames(
        css(props.showInstitutionFilter ? styles.shortInput : styles.tallInput),
        'u-small-spacing-bottom'
      )}
      placeholder="Search for an organization"
      size={Size.MEDIUM}
      value={props.query}
      onChange={handleChangeQuery}
      autoFocus={true}
    />
  );
}

function getAlreadyJoinedOrganizationIds(
  organizations: Array<JoinOrganizationListingOrganization>,
  currentUser: JoinOrganizationSearchUser | null
): Array<string> {
  const organizationIds = organizations.map(
    (organization: JoinOrganizationSearchUserOrganizaton) => organization.id
  );
  if (!currentUser) {
    return organizationIds;
  }
  assert(currentUser?.__typename === 'UserNode', `Non-user should not access this page.`);
  const userOrganizationIds = currentUser.organizations.map(
    (organization: JoinOrganizationSearchUserOrganizaton) => organization.id
  );
  return organizationIds.filter((organizationId: string) => userOrganizationIds.includes(organizationId));
}

export function JoinOrganizationSearchLoading(
  props: JoinOrganizationSearchBaseProps & {
    onClickRequestJoinOrganization: (organizationId: string) => void;
  }
): JSX.Element {
  return (
    <PresentationalJoinOrganizationSearch
      {...props}
      currentUser={null}
      organizations={[]}
      isLoading={true}
      showInstitutionFilter={props.showInstitutionFilter ?? false}
    />
  );
}

type PresentationalJoinOrganizationSearchProps = JoinOrganizationSearchBaseProps & {
  organizations: Array<JoinOrganizationListingOrganization>;
  onClickRequestJoinOrganization: (organizationId: string) => void;
  isLoading: boolean;
  currentUser: JoinOrganizationSearchUser | null;
  showInstitutionFilter: boolean;
  canNavigateToOrganizationPage?: boolean;
  headerText?: string;
};

export function PresentationalJoinOrganizationSearch(
  props: PresentationalJoinOrganizationSearchProps
): JSX.Element {
  const handleClickClear = (): void => {
    props.onChangeQuery('');
    props.setFilters([]);
  };

  const shouldShowPlaceholderText =
    props.query.length === 0 && props.organizations.length === 0 && props.placeholderBodyText != null;
  const debouncedHeaderText = useDebounce(props.headerText, LISTING_HEADER_DEBOUNCE_MS);
  const shouldShowClearButton =
    props.showInstitutionFilter && (props.query.length !== 0 || props.filters.length !== 0);
  return (
    <div {...getTestProps({component: 'PresentationalJoinOrganizationSearch', element: 'root'})}>
      <div className={css(styles.searchBars)}>
        <OrganizationSearchInput
          query={props.query}
          onChangeQuery={props.onChangeQuery}
          showInstitutionFilter={props.showInstitutionFilter}
        />
        {props.showInstitutionFilter && (
          <div className={css(styles.filterContainer)}>
            <FiltersContainer
              primaryFilterOptions={[{filterKey: FilterKey.INSTITUTION}]}
              filters={props.filters}
              onChangeFilters={props.setFilters}
              selectedSchema={null}
            />
            {shouldShowClearButton && (
              <span className={classNames(css(styles.clearIcon), 'u-no-wrap')}>
                <ClearSearchButton onClear={handleClickClear} />
              </span>
            )}
          </div>
        )}
      </div>

      <div
        className={css(styles.listing)}
        style={{height: props.listingHeight != null ? `${props.listingHeight}px` : 350}}
      >
        {shouldShowPlaceholderText ? (
          <div className={classNames(css(styles.bodyText), 'u-faded')}>{props.placeholderBodyText}</div>
        ) : props.isLoading ? (
          <CenteredHorizontalThrobber />
        ) : (
          <div>
            {debouncedHeaderText && (
              <p>
                <b>{debouncedHeaderText}</b>
              </p>
            )}
            <JoinOrganizationListing
              organizations={props.organizations}
              alreadyJoinedOrganizationIds={getAlreadyJoinedOrganizationIds(
                props.organizations,
                props.currentUser
              )}
              requestedJoinOrganizationIds={props.requestedJoinOrganizationIds}
              onClickRequestJoinOrganization={props.onClickRequestJoinOrganization}
              canNavigateToOrganizationPage={props.canNavigateToOrganizationPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  shortInput: {
    padding: '10px 10px',
    borderRadius: 5,
  },
  tallInput: {
    padding: '20px 10px',
    borderRadius: 5,
  },
  listing: {
    '@media (max-height: 640px)': {
      height: 200,
    },
    overflowY: 'auto',
    marginTop: '20px',
  },
  bodyText: {
    fontSize: '18px',
    lineHeight: '24px',
  },
  searchBars: {
    display: 'flex',
    gap: '10px',
  },
  filterContainer: {
    display: 'flex',
    gap: '10px',
    marginRight: '10px',
  },
  clearIcon: {
    fontSize: '12px',
    marginTop: '2px',
    lineHeight: '24px',
  },
});
